import CancellationsMessage from '@c/chat/CancellationsMessage';
import MessageAuto from '@c/chat/MessageAuto';
import MessageInbound from '@c/chat/MessageInbound';
import MessageMenu from '@c/chat/MessageMenu';
import MessageOffer from '@c/chat/MessageOffer';
import MessageOutbound from '@c/chat/MessageOutbound';
import MessagePaymentRequest from '@c/chat/MessagePaymentRequest';
import QuickReplies from '@c/chat/QuickReplies';
import ReturnDeliveredMessage from '@c/chat/ReturnDeliveredMessage';
import ReturnsMessage from '@c/chat/ReturnsMessage';
import type { ChatDocument, ChatMessageDocument } from '@models/chat';
import type { OfferDocument } from '@models/offer';
import { PAYMENT_REQUEST_MSG_PREFIX } from '@models/payment-request';
import { useQuery } from '@tanstack/react-query';
import { getProductById } from '@util/firestore/products';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useState } from 'react';

const CANCELLATION_MSG_RE =
  /^Hello[^,]*,\s+(?:the customer|a buyer) has requested to cancel/;
const RETURN_MSG_RE = /^The buyer has requested a return/;
const RETURN_DELIVERED_MSG_RE =
  /^Hey,\s+the buyer['’]s return has been delivered/;
const RELIST_MSG_RE = /(?:^|\n\n)Would you like to relist this item/i;

interface MessageBubbleProps {
  chat: ChatDocument;
  realtimeMessages?: ChatMessageDocument[];
  chatMessage: ChatMessageDocument;
  fallbackImg?: string;
  onImageClick: (image: string) => void;
  offer?: OfferDocument | null;
  isSidebar?: boolean;
  onQuickReply?: (text: string) => void;
}

export default function MessageBubble({
  chat,
  realtimeMessages,
  chatMessage,
  fallbackImg,
  onImageClick,
  offer,
  isSidebar,
  onQuickReply,
}: MessageBubbleProps) {
  const { userDoc } = useAuth();
  const { showSuccessToast } = useToastContext();

  const { data: product } = useQuery({
    queryKey: ['product', chat.product_id],
    queryFn: () => getProductById(chat.product_id ?? ''),
    enabled: !!chat.product_id,
  });

  // Use the offer prop passed from parent component
  const realtimeOffer: OfferDocument | null = offer ?? null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  if (!userDoc) return null;

  const onRightClick = (ev: React.MouseEvent) => {
    if (userDoc?.roles?.support) {
      ev.preventDefault();
      ev.stopPropagation();
      setMenuPosition({ x: ev.clientX, y: ev.clientY });
      setMenuOpen(true);
    }
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(chatMessage.content);
    showSuccessToast('Message copied to clipboard', 'bottom-center');
  };

  const isOutbound = chatMessage.uid === userDoc?.uid;
  const isAuto = chatMessage.is_auto;
  const hideMessage =
    isAuto &&
    chatMessage.auto_recipient &&
    chatMessage.auto_recipient !== userDoc.uid &&
    !userDoc.roles?.support;
  const isOfferMessage = chatMessage.content.startsWith('New Offer:');
  const isExclusiveOfferMessage = chatMessage.content.startsWith(
    'A seller sent you an exclusive offer'
  );
  const isPaymentRequestMessage =
    !!chatMessage.payment_request_id ||
    chatMessage.content.startsWith(PAYMENT_REQUEST_MSG_PREFIX);
  const isSpecialContextMsg =
    !chatMessage.is_img &&
    !isOfferMessage &&
    !isExclusiveOfferMessage &&
    !isPaymentRequestMessage;
  const isCancellationMsg =
    !isAuto &&
    isSpecialContextMsg &&
    CANCELLATION_MSG_RE.test(chatMessage.content);
  const isReturnMsg =
    !isAuto &&
    isSpecialContextMsg &&
    RETURN_MSG_RE.test(chatMessage.content);
  const isReturnDeliveredMsg =
    !isAuto &&
    isSpecialContextMsg &&
    RETURN_DELIVERED_MSG_RE.test(chatMessage.content);
  const isRelistMsg =
    isSpecialContextMsg && RELIST_MSG_RE.test(chatMessage.content);

  if (
    chatMessage.content.startsWith(
      'The seller sent you and other interested buyers'
    ) &&
    chat.is_offer &&
    chat.seller_id === userDoc?.uid
  )
    return null;

  const offerMessages = realtimeMessages?.filter(
    (m) =>
      m.offer_amount ||
      m.content.startsWith('New Offer: ') ||
      m.content.startsWith('A seller sent you an exclusive offer')
  );
  const currentOfferMessageIndex = offerMessages?.findIndex(
    (m) => m.created_at === chatMessage.created_at
  );

  // Handle offer messages
  if (isOfferMessage || isExclusiveOfferMessage) {
    return (
      <MessageOffer
        message={chatMessage}
        offer={realtimeOffer}
        isOldOffer={
          (offerMessages?.length ?? 0) - 1 !== currentOfferMessageIndex
        }
        isOutbound={isOutbound}
      />
    );
  }

  if (isPaymentRequestMessage) {
    return (
      <MessagePaymentRequest message={chatMessage} isOutbound={isOutbound} />
    );
  }

  const isLastMessage =
    realtimeMessages &&
    realtimeMessages[realtimeMessages.length - 1]?.created_at ===
      chatMessage.created_at;
  const hasQuickReplies =
    chatMessage.quick_replies &&
    chatMessage.quick_replies.length > 0 &&
    isLastMessage;
  const isStackedMsg =
    isCancellationMsg ||
    isReturnMsg ||
    isReturnDeliveredMsg ||
    isRelistMsg;

  return (
    <div className="grid">
      {isAuto ? (
        !hideMessage && (
          <MessageAuto message={chatMessage} onRightClick={onRightClick} />
        )
      ) : isCancellationMsg ? (
        <CancellationsMessage message={chatMessage} chat={chat} />
      ) : isReturnMsg ? (
        <ReturnsMessage message={chatMessage} chat={chat} />
      ) : isReturnDeliveredMsg ? (
        <ReturnDeliveredMessage message={chatMessage} chat={chat} />
      ) : isOutbound ? (
        <MessageOutbound
          message={chatMessage}
          timestamp={chatMessage.created_at ?? 0}
          fallbackImg={fallbackImg}
          onImageClick={onImageClick}
          onRightClick={onRightClick}
          isSidebar={isSidebar}
        />
      ) : (
        <MessageInbound
          message={chatMessage}
          timestamp={chatMessage.created_at ?? 0}
          fallbackImg={fallbackImg}
          onImageClick={onImageClick}
          onRightClick={onRightClick}
          isSidebar={isSidebar}
        />
      )}

      {hasQuickReplies && onQuickReply && (
        <QuickReplies
          title={chatMessage.quick_replies_title}
          quickReplies={chatMessage.quick_replies!}
          product={product}
          onQuickReply={onQuickReply}
          chat={chat}
          stacked={isStackedMsg}
        />
      )}

      <MessageMenu
        userDoc={userDoc}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        position={menuPosition}
        chat={chat}
        message={chatMessage}
        isOutbound={isOutbound}
        onCopyMessage={handleCopyMessage}
      />
    </div>
  );
}
